import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  PlusCircle,
  TrendingUp,
  History,
  Upload,
  ArrowDownToLine,
  ArrowUpFromLine,
  LogOut,
  LineChart,
  CandlestickChart,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import RatesTicker from "@/components/RatesTicker";
import MarketStatusDot from "@/components/MarketStatusDot";

const NAV = [
  { to: "/admin/dashboard",      icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/users",          icon: Users,           label: "Users" },
  { to: "/admin/markets",        icon: CandlestickChart, label: "Markets" },
  { to: "/admin/trades/add",     icon: PlusCircle,      label: "Add Trade" },
  { to: "/admin/trades/open",    icon: TrendingUp,      label: "Open Trades" },
  { to: "/admin/trades/history", icon: History,         label: "Trade History" },
  { to: "/admin/trades/bulk",    icon: Upload,          label: "Bulk Upload" },
  { to: "/admin/deposits",       icon: ArrowDownToLine, label: "Deposits" },
  { to: "/admin/withdrawals",    icon: ArrowUpFromLine, label: "Withdrawals" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const current = NAV.find((n) => location.pathname.startsWith(n.to));

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 shrink-0 border-r border-subtle bg-card flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-5 border-b border-subtle">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-primary flex items-center justify-center rounded">
              <LineChart className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-mono-num text-[10px] tracking-[0.2em] text-muted-foreground">
                ADMIN
              </div>
              <div className="font-bold text-base leading-none tracking-tight">XM FOREX</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary border-l-2 border-primary pl-[10px]"
                      : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-subtle">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-muted-foreground">Signed in as</div>
            <div className="text-sm font-medium truncate" data-testid="admin-user-name">
              {user?.full_name}
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded text-sm text-muted-foreground hover:text-loss hover:bg-surface-hover transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-subtle bg-card px-8 py-4 flex items-center justify-between" data-testid="admin-header">
          <div>
            <div className="label-eyebrow">Admin / {current?.label || "Dashboard"}</div>
            <h1 className="text-xl font-semibold tracking-tight">{current?.label || "Dashboard"}</h1>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <MarketStatusDot />
          </div>
        </header>
        <RatesTicker />
        <main className="flex-1 p-8 overflow-y-auto" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
